import { useEffect, useRef } from 'react'
import { UseWriteResult, useWrite } from '@/domain/hooks/useWrite'
import {
  Action,
  ActionHandler,
  ActionHandlerState,
  InitialParamsBase,
  VerifyTransactionResultBase,
} from '@/features/actions/logic/types'
import { mapWriteResultToActionState } from '@/features/actions/logic/utils'
import { QueryKey, queryOptions, skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApproveDelegationActionConfig } from '../flavours/approve-delegation/logic/approveDelegationAction'
import { createApproveActionConfig } from '../flavours/approve/logic/approveAction'
import { createBorrowActionConfig } from '../flavours/borrow/logic/borrowAction'
import { createClaimMarketRewardsActionConfig } from '../flavours/claim-market-rewards/logic/claimMarketRewardsAction'
import { createDepositActionConfig } from '../flavours/deposit/logic/depositAction'
import { createRepayActionConfig } from '../flavours/repay/logic/repayAction'
import { createSetUseAsCollateralActionConfig } from '../flavours/set-use-as-collateral/logic/setUseAsCollateralAction'
import { createSetUserEModeActionConfig } from '../flavours/set-user-e-mode/logic/setUserEModeAction'
import { createWithdrawActionConfig } from '../flavours/withdraw/logic/withdrawAction'
import {
  ActionConfig,
  ActionContext,
  GetWriteConfigResult,
  InitialParamsQueryResult,
  VerifyTransactionResult,
} from './types'
import { classifyError, trackAction } from '@/features/analytics'
import type { LendBorrowActionType, MarketType } from '@/features/analytics'
import { createIsolatedWithdrawActionConfig } from '../flavours/isolated-withdraw/logic/isolatedWithdrawAction'
import { createIsolatedLendWithdrawActionConfig } from '../flavours/isolated-lend-withdraw/logic/isolatedLendWithdrawAction'
import { createIsolatedLendDepositActionConfig } from '../flavours/isolated-lend-deposit/logic/isolatedLendDepositAction'
import { createIsolatedDepositActionConfig } from '../flavours/isolated-deposit/logic/isolatedDepositAction'
import { createIsolatedBorrowActionConfig } from '../flavours/isolated-borrow/logic/isolatedBorrowAction'
import { createIsolatedRepayActionConfig } from '../flavours/isolated-repay/logic/repayAction'
import { createVaultDepositActionConfig } from '../flavours/vault-deposit/logic/vaultDepositAction'
import { createVaultWithdrawActionConfig } from '../flavours/vault-withdraw/logic/vaultWithdrawAction'
import { createWrapActionConfig } from '../flavours/wrap/logic/wrapAction'

export interface UseContractActionParams {
  context: ActionContext
  action: Action
  enabled: boolean
}
export function useContractAction({ action, context, enabled }: UseContractActionParams): ActionHandler {
  const config = actionToConfig(action, context)
  const {
    initialParamsQueryOptions = defaultInitialParamsQueryOptions,
    getWriteConfig,
    verifyTransactionQueryOptions = defaultVerifyTransactionQueryOptions,
    invalidates,
  } = config

  const queryClient = useQueryClient()
  const submitTimeRef = useRef<number>(0)
  const activeAttemptRef = useRef(0)
  const finalizedAttemptRef = useRef(0)
  const lastSignedTxHashRef = useRef<string | undefined>(undefined)

  const initialParams = useQuery({
    ...initialParamsQueryOptions(),
    enabled,
  })

  const canBuildWriteConfig = initialParams.isSuccess && Boolean(initialParams.data)
  const writeConfig: GetWriteConfigResult = canBuildWriteConfig ? getWriteConfig(initialParams) : {}

  const write = useWrite(
    {
      ...writeConfig,
      enabled: enabled && canBuildWriteConfig,
    },
    {
      onBeforeWrite: () => {
        activeAttemptRef.current += 1
        finalizedAttemptRef.current = 0
        lastSignedTxHashRef.current = undefined
        submitTimeRef.current = Date.now()

        const analyticsMapping = mapActionToAnalytics(action)
        if (analyticsMapping) {
          trackAction('submitted', {
            ...analyticsMapping,
            token: getTokenFromAction(action),
            market: getMarketFromAction(action),
            amount: getAmountFromAction(action),
          })
        }
      },
      onTransactionSent: (txHash) => {
        const analyticsMapping = mapActionToAnalytics(action)
        if (!analyticsMapping || lastSignedTxHashRef.current === txHash) {
          return
        }

        lastSignedTxHashRef.current = txHash

        trackAction('signed', {
          ...analyticsMapping,
          token: getTokenFromAction(action),
          market: getMarketFromAction(action),
          amount: getAmountFromAction(action),
          tx_hash: txHash,
        })
      },
      onTransactionSettled: (txReceipt) => {
        invalidates().map((queryKey) => void queryClient.invalidateQueries({ queryKey }))

        const analyticsMapping = mapActionToAnalytics(action)
        const amount = getAmountFromAction(action)
        const token = getTokenFromAction(action)
        const market = getMarketFromAction(action)
        const durationMs = submitTimeRef.current > 0 ? Date.now() - submitTimeRef.current : 0

        if (!analyticsMapping || finalizedAttemptRef.current === activeAttemptRef.current) {
          context.txReceipts.push([action, txReceipt])
          return
        }

        finalizedAttemptRef.current = activeAttemptRef.current

        if (txReceipt.status === 'success') {
          trackAction('completed', {
            ...analyticsMapping,
            token,
            market,
            amount,
            tx_hash: txReceipt.transactionHash,
            duration_ms: durationMs,
          })
        } else {
          trackAction('failed', {
            ...analyticsMapping,
            token,
            market,
            amount,
            error_type: 'contract_revert',
            error_message: 'Transaction reverted',
            failed_at: 'confirmation',
          })
        }

        context.txReceipts.push([action, txReceipt])
      },
    },
  )

  useEffect(() => {
    if (write.status.kind !== 'error' || finalizedAttemptRef.current === activeAttemptRef.current) {
      return
    }

    const analyticsMapping = mapActionToAnalytics(action)
    if (!analyticsMapping) {
      return
    }

    finalizedAttemptRef.current = activeAttemptRef.current

    trackAction('failed', {
      ...analyticsMapping,
      token: getTokenFromAction(action),
      market: getMarketFromAction(action),
      amount: getAmountFromAction(action),
      error_type: classifyError(write.status.error),
      error_message: write.status.error.message,
      failed_at: write.status.errorKind === 'tx-reverted' || lastSignedTxHashRef.current ? 'confirmation' : 'sign',
    })
  }, [action, write.status])

  const verifyTransactionData = useQuery({
    ...verifyTransactionQueryOptions(),
    enabled: enabled && write.status.kind === 'success',
  })

  const state = mapStatusesToActionState({
    initialParams,
    write,
    verifyTransactionData,
    enabled,
  })

  return {
    action,
    state,
    onAction: write.write,
  }
}

interface MapStatusesToActionStateParams {
  initialParams: InitialParamsQueryResult
  write: UseWriteResult
  verifyTransactionData: VerifyTransactionResult
  enabled: boolean
}

export function mapActionToAnalytics(action: Action): { action_type: LendBorrowActionType; market_type: MarketType } | null {
  switch (action.type) {
    case 'deposit': return { action_type: 'deposit', market_type: 'standard' }
    case 'borrow': return { action_type: 'borrow', market_type: 'standard' }
    case 'repay': return { action_type: 'repay', market_type: 'standard' }
    case 'withdraw': return { action_type: 'withdraw', market_type: 'standard' }
    case 'isolatedDeposit':
    case 'isolatedLendDeposit': return { action_type: 'deposit', market_type: 'isolated' }
    case 'isolatedBorrow': return { action_type: 'borrow', market_type: 'isolated' }
    case 'isolatedRepay': return { action_type: 'repay', market_type: 'isolated' }
    case 'isolatedWithdraw':
    case 'isolatedLendWithdraw': return { action_type: 'withdraw', market_type: 'isolated' }
    case 'vaultDeposit': return { action_type: 'deposit', market_type: 'vault' }
    case 'vaultWithdraw': return { action_type: 'withdraw', market_type: 'vault' }
    default: return null // approve, permit, bridge, wrap, etc. — not tracked
  }
}

function getTokenFromAction(action: Action): string {
  if (action.type === 'repay') return String(action.reserve.token.symbol)
  if ('token' in action) return String(action.token.symbol)
  return 'unknown'
}

function getMarketFromAction(action: Action): string {
  if (action.type === 'vaultDeposit' || action.type === 'vaultWithdraw') return String(action.vaultAddress)
  if (action.type === 'repay') return String(action.reserve.token.address)
  if ('token' in action) return String(action.token.address)
  return 'unknown'
}

function getAmountFromAction(action: Action): string {
  if ('value' in action) return action.value.toFixed()
  return '0'
}

function mapStatusesToActionState({
  initialParams,
  write,
  verifyTransactionData,
  enabled,
}: MapStatusesToActionStateParams): ActionHandlerState {
  if (!enabled) {
    return { status: 'disabled' }
  }

  if (initialParams.fetchStatus === 'fetching') {
    return { status: 'loading' }
  }

  if (initialParams.status === 'error') {
    return { status: 'error', errorKind: 'initial-params', message: initialParams.error.message }
  }

  if (initialParams.data?.canBeSkipped && initialParams.fetchStatus === 'idle') {
    return { status: 'success' }
  }

  if (verifyTransactionData.fetchStatus === 'fetching') {
    return { status: 'loading' }
  }

  if (verifyTransactionData.status === 'error') {
    return { status: 'error', errorKind: 'tx-verify', message: verifyTransactionData.error.message }
  }

  // Transaction result didn't reach it's objective,
  // for instance user went through the approval flow but manually tweaked approval level
  // and it's still too low
  if (write.status.kind === 'success' && verifyTransactionData.data?.success === false) {
    return { status: 'ready' }
  }

  return mapWriteResultToActionState(write)
}

function actionToConfig(action: Action, context: ActionContext): ActionConfig {
  switch (action.type) {
    case 'approve':
      return createApproveActionConfig(action, context)
    case 'deposit':
      return createDepositActionConfig(action, context)
    case 'borrow':
      return createBorrowActionConfig(action, context)
    case 'setUseAsCollateral':
      return createSetUseAsCollateralActionConfig(action, context)
    case 'approveDelegation':
      return createApproveDelegationActionConfig(action, context)
    case 'setUserEMode':
      return createSetUserEModeActionConfig(action, context)
    case 'repay':
      return createRepayActionConfig(action, context)
    case 'claimMarketRewards':
      return createClaimMarketRewardsActionConfig(action, context)
    case 'withdraw':
      return createWithdrawActionConfig(action, context)
    case 'permit':
    case 'permit2Sign':
      // Permit2Sign actions use a separate flow with typed data signing, not contract calls
      return createEmptyActionConfig()
    case 'bridgeToHyperEVM':
    case 'bridgeToHyperCore':
      // Bridge actions use a separate flow with custom contract calls
      return createEmptyActionConfig()
    case 'isolatedWithdraw':
      return createIsolatedWithdrawActionConfig(action, context)
    case 'isolatedLendWithdraw':
      return createIsolatedLendWithdrawActionConfig(action, context)
    case 'isolatedLendDeposit':
      return createIsolatedLendDepositActionConfig(action, context)
    case 'isolatedDeposit':
      return createIsolatedDepositActionConfig(action, context)
    case 'isolatedBorrow':
      return createIsolatedBorrowActionConfig(action, context)
    case 'isolatedRepay':
      return createIsolatedRepayActionConfig(action, context)
    case 'vaultDeposit':
      return createVaultDepositActionConfig(action, context)
    case 'vaultWithdraw':
      return createVaultWithdrawActionConfig(action, context)
    case 'wrap':
      return createWrapActionConfig(action, context)
    default: {
      console.warn(`Unhandled action type: ${(action as Action).type}`)
      return createEmptyActionConfig()
    }
  }
}

function createEmptyActionConfig(): ActionConfig {
  return {
    initialParamsQueryOptions: () => ({ queryKey: [], queryFn: skipToken }),
    getWriteConfig: () => ({}),
    verifyTransactionQueryOptions: () => ({ queryKey: [], queryFn: skipToken }),
    invalidates: () => [],
    beforeWriteCheck: () => {},
  }
}

function defaultInitialParamsQueryOptions() {
  return queryOptions<InitialParamsBase>({
    queryKey: ['default-initial-params'] as QueryKey,
    queryFn: () => ({ canBeSkipped: false }),
  })
}

function defaultVerifyTransactionQueryOptions() {
  return queryOptions<VerifyTransactionResultBase>({
    queryKey: ['default-verify-transaction'] as QueryKey,
    queryFn: () => ({ success: true }),
  })
}
